import type { RedFlag, ObservableSign, PlayerSymptom, OutcomeAction } from "../types/assessment.types";

// 10 CRT6 red flags (F-010 AC-080)
export const RED_FLAGS: RedFlag[] = [
  {
    id: "neck-pain",
    label: "Neck pain or tenderness",
  },
  {
    id: "double-vision",
    label: "Loss of vision or double vision",
  },
  {
    id: "weakness-tingling",
    label: "Weakness or numbness/tingling in more than one arm or leg",
  },
  {
    id: "severe-headache",
    label: "Severe or increasing headache",
  },
  {
    id: "seizure",
    label: "Seizure, 'fits', or convulsion",
  },
  {
    id: "loss-consciousness",
    label: "Loss of consciousness",
  },
  {
    id: "deteriorating-consciousness",
    label: "Increased confusion or deteriorating conscious state (becoming less responsive, drowsy)",
  },
  {
    id: "vomiting",
    label: "Repeated vomiting",
  },
  {
    id: "increasingly-restless",
    label: "Increasingly restless, agitated or combative",
  },
  {
    id: "skull-deformity",
    label: "Visible deformity of the skull",
  },
];

// 7 CRT6 visible clues (F-011 AC-088) — what the trainer can see
export const OBSERVABLE_SIGNS: ObservableSign[] = [
  {
    id: "lying-motionless",
    label: "Lying motionless on the playing surface",
  },
  {
    id: "falling-unprotected",
    label: "Falling unprotected to the playing surface",
  },
  {
    id: "disorientation",
    label: "Disorientation or confusion, staring or limited responsiveness, or an inability to respond appropriately to questions",
  },
  {
    id: "blank-vacant",
    label: "Dazed, blank or vacant look",
  },
  {
    id: "slow-to-get-up",
    label: "Slow to get up after a direct or indirect hit to the head",
  },
  {
    id: "balance-gait",
    label: "Unsteady on feet / balance problems or falling over / poor coordination / wobbly",
  },
  {
    id: "facial-injury",
    label: "Facial injury",
  },
];

// 9 CRT6 player-reported symptoms (F-011 AC-089) — ask the player
export const SELF_SYMPTOMS: PlayerSymptom[] = [
  {
    id: "headache",
    label: "Headache",
    question: "Do you have a headache?",
  },
  {
    id: "dizziness",
    label: "Dizziness",
    question: "Do you feel dizzy or off-balance?",
  },
  {
    id: "nausea",
    label: "Nausea",
    question: "Do you feel sick to your stomach?",
  },
  {
    id: "light-sensitivity",
    label: "Sensitivity to light",
    question: "Does light bother your eyes?",
  },
  {
    id: "noise-sensitivity",
    label: "Sensitivity to noise",
    question: "Do sounds seem too loud or bothersome?",
  },
  {
    id: "balance-problems",
    label: "Balance problems",
    question: "Are you having trouble balancing?",
  },
  {
    id: "fatigue",
    label: "Fatigue or tiredness",
    question: "Do you feel unusually tired or sleepy?",
  },
  {
    id: "difficulty-concentrating",
    label: "Difficulty concentrating",
    question: "Is it hard to focus or pay attention?",
  },
  {
    id: "feeling-foggy",
    label: "Feeling foggy",
    question: "Do you feel like you're in a fog or not quite yourself?",
  },
];

// 5 CRT6 awareness questions (F-012) — cognitive orientation assessment
export const AWARENESS_QUESTIONS = [
  { id: "aw-location", label: "Where are we today?" },
  { id: "aw-event", label: "What event were you doing?" },
  { id: "aw-scored-last", label: "Who scored last in this game?" },
  { id: "aw-last-opponent", label: "What team did you play last week/game?" },
  { id: "aw-last-result", label: "Did your team win the last game?" },
];

// Observable emotion symptoms (F-011) — assessor to determine
export const EMOTION_SYMPTOMS = [
  { id: "obs-more-emotional", label: "More emotional" },
  { id: "obs-more-irritable", label: "More irritable" },
  { id: "obs-sadness", label: "Sadness" },
  { id: "obs-nervous-anxious", label: "Nervous or anxious" },
];

// Observable thinking symptoms (F-011) — assessor to determine
export const THINKING_SYMPTOMS = [
  { id: "obs-difficulty-concentrating", label: "Difficulty concentrating" },
  { id: "obs-difficulty-remembering", label: "Difficulty remembering" },
  { id: "obs-feeling-slowed-down", label: "Feeling slowed down" },
  { id: "obs-feeling-in-fog", label: `Feeling like "in a fog"` },
];

export const OUTCOMES: Record<string, OutcomeAction> = {
  emergency: {
    type: "emergency",
    title: "EMERGENCY - Call Triple Zero (000)",
    description:
      "Red flags detected. This person needs immediate emergency medical attention. Do NOT move them unless necessary for safety.",
    actions: [
      "Call Triple Zero (000) immediately",
      "Do NOT move the person unless in danger",
      "Keep them still and monitor breathing",
      "Stay with them until paramedics arrive",
      "Do NOT remove helmet (if wearing one)",
    ],
    color: "#EF4444",
  },
  possible_concussion: {
    type: "possible_concussion",
    title: "Possible Concussion - Remove From Play",
    description:
      "Observable signs of concussion detected. The player must be immediately and permanently removed from play today.",
    actions: [
      "Remove player from play IMMEDIATELY",
      "Do NOT allow return to play today",
      "Notify parent/guardian/emergency contact",
      "Recommend medical assessment within 24 hours",
      "Monitor for worsening symptoms",
      "Do not leave the person alone",
    ],
    color: "#F59E0B",
  },
  monitoring: {
    type: "monitoring",
    title: "Symptoms Reported - Remove From Play",
    description:
      "The player has reported concussion symptoms. They must not return to play today and should be closely monitored for the next 24-48 hours.",
    actions: [
      "Remove player from play for the rest of the day",
      "Do NOT allow return to play today",
      "Monitor closely for worsening symptoms over 24-48 hours",
      "Inform parent/guardian of the player's symptoms",
      "Recommend medical assessment if symptoms persist or worsen",
      "If in doubt, sit them out",
    ],
    color: "#F59E0B",
  },
  no_signs: {
    type: "no_signs",
    title: "No Signs Detected",
    description:
      "No observable signs or reported symptoms at this time. Symptoms can appear hours later — continue to monitor the player.",
    actions: [
      "Continue monitoring during the game",
      "Re-assess if the player reports any symptoms",
      "Inform parent/guardian of the incident",
      "Remember: if in doubt, sit them out",
    ],
    color: "#10B981",
  },
};
